/**
 * AdminLayout - Layout for admin dashboard with sidebar navigation
 */
import React, { memo, useState, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import {
  FiHome,
  FiPackage,
  FiGrid,
  FiAward,
  FiShoppingCart,
  FiUsers,
  FiSettings,
  FiMail,
  FiTag,
  FiLogOut,
  FiMenu,
  FiX,
  FiChevronLeft,
} from 'react-icons/fi';
import clsx from 'clsx';

import styles from './AdminLayout.module.css';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { path: ROUTES.ADMIN_DASHBOARD, label: 'Dashboard', icon: <FiHome /> },
  { path: ROUTES.ADMIN_PRODUCTS, label: 'Products', icon: <FiPackage /> },
  { path: ROUTES.ADMIN_CATEGORIES, label: 'Categories', icon: <FiGrid /> },
  { path: ROUTES.ADMIN_BRANDS, label: 'Brands', icon: <FiAward /> },
  { path: ROUTES.ADMIN_COUPONS, label: 'Coupons', icon: <FiTag /> },
  { path: ROUTES.ADMIN_ORDERS, label: 'Orders', icon: <FiShoppingCart /> },
  { path: ROUTES.ADMIN_INQUIRIES, label: 'Inquiries', icon: <FiMail /> },
  { path: ROUTES.ADMIN_USERS, label: 'Users', icon: <FiUsers /> },
  { path: ROUTES.ADMIN_SETTINGS, label: 'Settings', icon: <FiSettings /> },
];

const AdminLayout: React.FC = memo(() => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  return (
    <div className={styles.layout}>
      {/* Desktop Sidebar */}
      <aside
        className={clsx(styles.sidebar, !isSidebarOpen && styles.sidebarCollapsed)}
      >
        <div className={styles.sidebarHeader}>
          <NavLink to={ROUTES.ADMIN_DASHBOARD} className={styles.logo}>
            {isSidebarOpen ? (
              <span className={styles.logoText}>Sunvivo Admin</span>
            ) : (
              <span className={styles.logoIcon}>S</span>
            )}
          </NavLink>
          <button
            className={styles.collapseBtn}
            onClick={toggleSidebar}
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <FiChevronLeft
              className={clsx(styles.collapseIcon, !isSidebarOpen && styles.rotated)}
            />
          </button>
        </div>

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                clsx(styles.navItem, isActive && styles.navItemActive)
              }
              title={!isSidebarOpen ? item.label : undefined}
            >
              <span className={styles.navIcon}>{item.icon}</span>
              {isSidebarOpen && <span className={styles.navLabel}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <button
            className={styles.backToSite}
            onClick={() => navigate(ROUTES.HOME)}
            title={!isSidebarOpen ? 'Back to Site' : undefined}
          >
            <FiChevronLeft />
            {isSidebarOpen && <span>Back to Site</span>}
          </button>
          <button
            className={styles.logoutBtn}
            onClick={handleLogout}
            title={!isSidebarOpen ? 'Logout' : undefined}
          >
            <FiLogOut />
            {isSidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={styles.mainWrapper}>
        {/* Header */}
        <header className={styles.header}>
          <button
            className={styles.mobileMenuBtn}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <FiMenu />
          </button>

          <NavLink to={ROUTES.ADMIN_DASHBOARD} className={styles.headerLogo}>
            <span className={styles.headerLogoIcon}>S</span>
            <span className={styles.headerLogoText}>Sunvivo Admin</span>
          </NavLink>

          <div className={styles.headerTitle}>Admin Panel</div>

          <div className={styles.headerRight}>
            <span className={styles.adminName}>
              {user?.displayName || 'Admin'}
            </span>
          </div>
        </header>

        {/* Main Content */}
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className={styles.overlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeMobileMenu}
            />
            <motion.aside
              className={styles.mobileSidebar}
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            >
              <div className={styles.mobileSidebarHeader}>
                <NavLink to={ROUTES.ADMIN_DASHBOARD} className={styles.logo} onClick={closeMobileMenu}>
                  <span className={styles.logoText}>Sunvivo Admin</span>
                </NavLink>
                <button
                  className={styles.closeBtn}
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <FiX />
                </button>
              </div>

              <nav className={styles.mobileNav}>
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      clsx(styles.mobileNavItem, isActive && styles.mobileNavItemActive)
                    }
                    onClick={closeMobileMenu}
                  >
                    <span className={styles.navIcon}>{item.icon}</span>
                    <span className={styles.navLabel}>{item.label}</span>
                  </NavLink>
                ))}
              </nav>

              <div className={styles.mobileSidebarFooter}>
                <button
                  className={styles.backToSite}
                  onClick={() => {
                    closeMobileMenu();
                    navigate(ROUTES.HOME);
                  }}
                >
                  <FiChevronLeft />
                  <span>Back to Site</span>
                </button>
                <button
                  className={styles.logoutBtn}
                  onClick={() => {
                    closeMobileMenu();
                    handleLogout();
                  }}
                >
                  <FiLogOut />
                  <span>Logout</span>
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1E293B',
            color: '#F1F5F9',
            borderRadius: '12px',
            padding: '16px',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#FFFFFF',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#FFFFFF',
            },
          },
        }}
      />
    </div>
  );
});

AdminLayout.displayName = 'AdminLayout';

export default AdminLayout;
